import clsx from "clsx";

export function Task({
  complete,
  total,
  children,
}: {
  complete: number;
  total: number;
  children: React.ReactNode;
}) {
  return (
    <p
      className={clsx(
        "mt-4 p-4 rounded-md",
        complete < total && "light:bg-yellow-100 dark:bg-yellow-600",
        complete === total && "light:bg-green-100 dark:bg-green-600",
      )}
    >
      {complete < total ? "To do" : "✅"} [{complete} / {total} complete]:{" "}
      {children}
    </p>
  );
}

export function RecusalInstructions({
  votedOn,
  totalToVoteOn,
  needsDisclosure,
  totalProposals,
  showTasks,
  forOnboarding = false,
}: {
  votedOn?: number;
  totalToVoteOn?: number;
  needsDisclosure: number;
  totalProposals: number;
  showTasks: boolean;
  forOnboarding: boolean;
}) {
  return (
    <div className="py-4 max-w-3xl">
      <h3 className="text-xl font-semibold">Definitions</h3>
      <p className="mt-4">
        A <em>disclosure</em> is information you provide about your relationship
        to an org, which might lead to your recusal or might not. By default, we
        share disclosure information within the group (Recommenders, organizers,
        and Funders) but not further.
      </p>
      <p className="mt-4">
        Being <em>recused</em> from evaluating an org means
      </p>
      <ul className="ml-6 list-[lower-alpha] list-outside">
        <li>
          You do not enter numerical evaluations for the org, but you're allowed
          to write notes about them for others to read at their leisure.
        </li>
        <li>
          You do not proactively contribute or "push" information about the org
          in meetings, but you are allowed to answer questions about the org if
          other Recommenders choose to "pull" information from you by asking
          questions about it.
        </li>
        <li>
          You can be asked to leave the "room" at any time by anyone on the
          call, at which point you leave the video call and immediately re-join
          the "waiting room" for the call until the moderator lets you back in.
          If a lot of money is going to the org, this should probably happen at
          some point, maybe multiple times.
        </li>
        <li>
          You cannot serve as an "investigator" for the org, i.e., you cannot
          contact the org on behalf of the round for more information.
        </li>
      </ul>

      <h3 className="text-xl font-semibold mt-4">Ethos / spirit</h3>
      <p className="mt-4">
        The general ethos of the disclosure/recusal process is that it allows
        people other than you to decide if you should be trusted to evaluate a
        given org, and it gives them space to talk about the org without you
        around. You can also recuse yourself.
      </p>
      {forOnboarding && (
        <p className="mt-4">
          The process of voting to recuse someone is discursive, in that we
          discuss our votes and change them in response to arguments until we
          decide we're done (before the end of the onboarding meeting). If the
          group thinks you're self-recusing too much, they might talk you out of
          it; on the other hand, you might update from their votes that you
          should have self-recused on more orgs.
        </p>
      )}

      <h3 className="text-xl font-semibold mt-4">Disclosure instructions</h3>
      {showTasks && (
        <Task
          complete={totalProposals - needsDisclosure}
          total={totalProposals}
        >
          {forOnboarding
            ? "At least a week before the onboarding meeting (or other deadline set by organizers), look through orgs listed below to identify the ones that you have a relationship with, and enter a disclosure for them."
            : "Look through orgs listed below to identify the ones that you have a relationship with, and enter a disclosure for them."}
        </Task>
      )}
      {showTasks && totalToVoteOn && votedOn && (
        <Task complete={votedOn} total={totalToVoteOn}>
          Vote on the recusals of other Recommenders.
        </Task>
      )}

      <ul className="mt-4 ml-6 list-outside list-disc">
        <li>Q: How many orgs should you enter disclosures for?</li>
        <li>
          A: Here's a rule of thumb to get started: if you feel there are N orgs
          that you should definitely self-recuse from, then try to enter an
          additional 0.5N to N disclosures for the orgs that you feel rank next
          in terms of "how much" you should be recused. This should give the
          group a chance to give you feedback on your judgement, via their votes
          on those marginal cases.
        </li>
      </ul>
    </div>
  );
}

export function InterestInstructions({
  complete,
  total,
  showTasks,
}: {
  complete: number;
  total: number;
  showTasks: boolean;
}) {
  return (
    <div className="py-4 max-w-3xl">
      <h3 className="text-xl font-semibold">
        Instructions for indicating your investigation interest
      </h3>
      <p className="mt-4">
        Roughly speaking, if an org gets money, it’s good if at least one
        Recommender "investigated" them (e.g., called them to ask them
        questions, did a deep dive into their history or research, etc.). It's
        good if an org is investigated either by Recommenders who support them
        heavily, or Recommenders who are curious about the org and want to bring
        in fresh perspectives on them, and ideally both.
      </p>
      <p className="mt-4">
        How will you/we know if an org is going to get money? We won’t know for
        sure, because Funders get a chance to shift things around at the end;
        this is just a rule of thumb / heuristic to help us work together on
        getting a good amount of investigation done.
      </p>
      {showTasks && (
        <Task complete={complete} total={total}>
          Before the first meeting, enter your interest in investigating each
          particular organisation as a number from 0-4 under your intials.
        </Task>
      )}

      <p className="mt-4">Investigation interest levels:</p>
      <ul className="ml-6 list-outside list-disc">
        <li>0 = definitely don't want to investigate (p ~ 0%)</li>
        <li>1 = probably don't want to investigate (p ≤ 20%)</li>
        <li>2 = maybe want to investigate (p ~ 50%)</li>
        <li>3 = probably want to investigate (p ≥ 80%)</li>
        <li>4 = definitely want to investigate (p ~ 100%)</li>
      </ul>
    </div>
  );
}

export function ReviewProgressInstructions({
  complete,
  total,
  showTasks,
}: {
  complete: number;
  total: number;
  showTasks: boolean;
}) {
  return (
    <div className="py-4 max-w-3xl">
      <h3 className="text-xl font-semibold">
        Instructions for entering your review progress
      </h3>
      <p className="mt-4">
        Periodically, after you begin reviewing applicants, enter your progress
        levels (described below) under your initials. We'll use this information
        in meetings to call attention to applicants who should get more
        attention, either because they're neglected or projected to get a lot of
        funding.
      </p>
      {showTasks && (
        <Task complete={complete} total={total}>
          Ensure you at least skim read every non-late application.
        </Task>
      )}

      <p className="mt-4">Review progress levels:</p>
      <ul className="ml-6 list-outside list-disc">
        <li>1 = skim read: skimmed the application materials.</li>
        <li>
          2 = thorough read: read most or all of the application, including
          long-form.
        </li>
        <li>3 = wrote notes</li>
        <li>
          4 = investigated (e.g. had a call, deep dive into their history)
        </li>
      </ul>
    </div>
  );
}
